// Prompts IA éditables pour la génération des pages de proposition.
// Pour changer le comportement par défaut de façon permanente, modifie la constante
// ci-dessous. Le prompt est aussi affiché (et modifiable ponctuellement) dans le
// formulaire super-admin > contenu.
//
// Le placeholder {{brief}} est remplacé par la demande initiale collée dans le
// formulaire. S'il est absent, la demande est ajoutée à la fin du prompt.
//
// Non-régression après modification d'un prompt : aucune suite de tests
// automatisée n'existe sur ce pipeline (sorties non déterministes, coût API).
// À la place, avant de committer un changement sur hero, messages ou enjeux,
// relance-le manuellement sur 2-3 briefs de référence bien distincts (ex : un
// SaaS B2B technique, une plateforme grand public, une automatisation
// interne) et relis le résultat en te posant la question de l'étape
// "VÉRIFICATION ANTI-GÉNÉRIQUE" du prompt lui-même. Un changement qui améliore
// un brief-type et en dégrade un autre n'est pas un progrès net.

export const BRIEF_PLACEHOLDER = "{{brief}}";
export const YEAR_PLACEHOLDER = "{{year}}";
// {{enjeux}} = enjeux stratégiques du projet (data.strategicStakes). Réinjecté
// dans les générations en aval pour les garder cohérentes entre elles.
export const ENJEUX_PLACEHOLDER = "{{enjeux}}";
// {{url_proposition}} = URL publique de la page de proposition.
export const URL_PROPOSITION_PLACEHOLDER = "{{url_proposition}}";
// {{messaging_house}} = banque de valeur (data.messagingHouse). Seule source
// autorisée pour tout engagement / garantie / méthode citée dans les messages.
export const MESSAGING_HOUSE_PLACEHOLDER = "{{messaging_house}}";
// {{sections_disponibles}} = sections réellement présentes sur la page à
// l'instant de la génération. Le modèle ne doit annoncer que celles-ci.
export const SECTIONS_DISPONIBLES_PLACEHOLDER = "{{sections_disponibles}}";
// {{budget}} = tranche de budget sélectionnée par le prospect sur la plateforme
// de freelancing (pas forcément écrite dans le brief). Sert à décider si la FAQ
// inclut une question sur les facilités de paiement.
export const BUDGET_PLACEHOLDER = "{{budget}}";
// {{realisations}} = résumé de la banque de réalisations Léman IT
// (src/lib/content/realisations-bank.ts). Seule source autorisée pour l'étude de
// cas : le modèle choisit un id existant et n'invente ni projet ni chiffre.
export const REALISATIONS_PLACEHOLDER = "{{realisations}}";
// {{fourchette_prix}} / {{fourchette_delai}} = fourchette réelle de la grille
// tarifaire (data.pricingGrid, lignes "base" uniquement), déjà formatée en
// français (ex : "4 700 € – 7 050 €", "13 jours – 20 jours"). Seule source
// autorisée pour tout prix ou délai cité dans le teaser Codeur.com : jamais
// calculée ni estimée par le modèle lui-même. Valeur de repli explicite si la
// grille n'est pas encore disponible (voir buildPromptWithBrief) : le prompt
// doit alors sauter tout le paragraphe commercial plutôt que d'improviser.
export const PRICE_RANGE_PLACEHOLDER = "{{fourchette_prix}}";
export const DELAY_RANGE_PLACEHOLDER = "{{fourchette_delai}}";

// Règles communes à TOUS les prompts de génération, factorisées ici pour
// qu'un ajout (ex : un nouveau tic rédactionnel IA à bannir) se propage
// automatiquement partout au lieu de devoir être recopié dans 7 endroits
// (source de la dérive déjà observée entre les deux prompts de maquettes).
// Interpolée directement dans chaque DEFAULT_*_PROMPT via ${COMMON_AI_RULES} :
// ce n'est PAS un placeholder {{...}} résolu à l'exécution, la substitution a
// lieu une fois pour toutes au chargement du module.
export const COMMON_AI_RULES = `Règles communes, sans exception :
- Français. Aucun tiret cadratin ni demi-cadratin (— ou –), jamais : utilise
  deux-points, virgule ou parenthèses à la place.
- Toujours "je", jamais "nous" / "notre" / "nos" pour parler de qui répond au
  besoin : c'est un développeur freelance qui écrit, pas une agence. Après
  "je", un vrai verbe conjugué à la première personne du singulier doit
  suivre, jamais un nom qui y ressemble ("je cadrage", "je structuration") ni
  un reste de conjugaison "nous" ("je cadrons", "je structurons") : la forme
  correcte est "je cadre", "je structure". Relis chaque phrase commençant par
  "je" et vérifie que le mot juste après est bien un verbe à cette forme,
  avant de répondre.
- Aucune construction en contraste binaire du type "Ce n'est pas X, c'est Y",
  "X n'est pas le problème, Y l'est", "La question n'est pas X, c'est Y", "Il
  semble que X, en réalité Y" : énonce Y directement, sans détour rhétorique.
- Fausse agence interdite : ne donne jamais un verbe humain à une chose
  inanimée ("la confiance grandit", "l'enjeu se résout", "le marché
  récompense"). Nomme toujours qui agit ("vous gagnez la confiance de...", "je
  résous cet enjeu en...").
- Aucune formule automatique de type IA : "révolutionner", "propulser", "à la
  pointe", "sans précédent", "dans un monde en constante évolution",
  "exploiter tout le potentiel", "plonger dans", "un véritable", "à l'ère du
  digital", "levier" (au sens figuré). Aucune structure "que vous soyez X, Y
  ou Z". Même bannissement pour "évolutif", "robuste", "innovant", "fluide",
  "performant" employés seuls, comme qualificatif vague : n'importe lequel de
  ces mots reste utilisable UNIQUEMENT accompagné du mécanisme concret qui le
  justifie (ex : pas "une architecture robuste" seul, mais "une architecture
  qui isole les données de chaque propriétaire au niveau de la base").
- Varie le rythme des phrases : alterne courtes et longues, jamais trois
  phrases de suite de longueur et de structure identiques.
- Aucune métrique, statistique ou durée d'expérience inventée.`;

/**
 * Grille de vérification "anti-slop" en 5 axes chiffrés, utilisée à la place
 * d'une simple question fermée ("est-ce générique, oui/non ?") : une question
 * binaire est trop facile à valider d'un "non" complaisant sans vraie
 * relecture. Noter chaque axe force à considérer séparément ce qui rend un
 * texte IA reconnaissable (direction évasive, rythme monotone, prudence
 * excessive, absence de détail réel, remplissage). Seuil de révision : 35/50,
 * repère volontairement strict.
 */
export function buildAntiSlopCheck(subject: string): string {
  return `VÉRIFICATION ANTI-GÉNÉRIQUE (avant de répondre)
Note ${subject} sur chacun des 5 axes suivants, de 0 à 10 (note interne, jamais
renvoyée dans le JSON) :
- Direction : tranche-t-il sur un angle précis, ou reste-t-il assez flou pour
  convenir à n'importe quel projet du secteur ?
- Rythme : la longueur et la structure des phrases varient-elles, ou
  s'alignent-elles toutes sur le même schéma ?
- Confiance : le ton affirme-t-il, ou multiplie-t-il les précautions
  ("pourrait", "peut-être", "dans une certaine mesure") ?
- Authenticité : cite-t-il un détail que seul CE brief pouvait donner, ou
  resterait-il identique mot pour mot pour n'importe quel autre projet du
  même secteur ?
- Densité : chaque mot porte-t-il de l'information, ou une partie n'est-elle
  que du remplissage qui n'ajoute rien ?
Additionne les 5 notes. En dessous de 35/50, réécris avant de répondre : ce
seuil signale un texte trop générique, trop plat ou trop prudent pour cette
page.`;
}

/**
 * Détection de style de communication DISC (Dominance / Influence / Stabilité
 * / Conformité), reprise du skill "disc-profiler". Cadrage scientifique
 * respecté : DISC n'est pas un instrument psychométrique validé et un texte
 * non structuré prédit mal la personnalité réelle (voir la doc du skill) ;
 * ceci reste une hypothèse de STYLE DE COMMUNICATION PERÇU, jamais un
 * diagnostic de personnalité. Interpolée dans hero et FAQ pour calibrer le
 * ton et la structure à partir du même brief, chaque prompt en tire ensuite
 * ses propres règles d'écriture (voir leurs sections dédiées).
 */
export const DISC_STYLE_ANALYSIS = `DÉTECTION DU STYLE DE COMMUNICATION (à restituer dans "profile" et dans
disc_style / disc_secondary / disc_confidence)
Ceci n'est PAS un diagnostic de personnalité : une hypothèse sur le style de
communication perçu à l'écrit, pour calibrer le ton de la réponse. N'écris
jamais "cette personne est...", "son caractère", "profil de personnalité" :
dis "le brief donne une impression de...".

Repère ces marqueurs dans le brief :
- D (Dominance : résultat, contrôle, rapidité) : message court, direct,
  formulations impératives ("il me faut", "je veux"), peu de contexte
  émotionnel, délais mentionnés tôt et fermement, questions fermées
  ("combien", "quand"), signature minimale.
- I (Influence : relationnel, enthousiasme) : ton enthousiaste, points
  d'exclamation, anecdotes ou digressions, vocabulaire imagé, mentions du
  relationnel futur, idées enchaînées par association plutôt que par plan.
- S (Stabilité : coopération, prudence) : rythme posé, ouverture par une prise
  de nouvelles, besoin de réassurance exprimé, nuance beaucoup ("peut-être",
  "si possible"), évite les formulations tranchées.
- C (Conformité : précision, méthode) : message structuré (listes, sous-
  parties), beaucoup de détails techniques et de chiffres, ton neutre,
  questions de vérification ("pouvez-vous préciser", "quelles garanties"),
  souci d'exhaustivité.

Identifie un style dominant (disc_style) et, si les indices le justifient,
une composante secondaire (disc_secondary, sinon null). Jamais de score
chiffré ("73% D") : une dominante peut avoir une composante secondaire, pas un
pourcentage. Cite dans "profile" 2-3 indices concrets du brief qui justifient
ce choix. Un brief de plateforme freelance est court par nature : mets
disc_confidence à "faible" par défaut, "modere" seulement si le brief est
particulièrement long ou détaillé pour ce type de plateforme. Si les indices
sont trop rares ou contradictoires pour trancher, choisis quand même le style
le plus probable plutôt que de forcer un résultat incohérent, mais reflète
cette incertitude dans "profile" et mets disc_confidence à "faible".`;

// Banque de valeur par défaut (messaging house Léman IT). Chaque ligne =
// Problem (ce que le client craint) / Feature / Benefit. En anglais : le prompt
// demande au modèle de traduire et reformuler, jamais de recopier le libellé.
// Éditable par offre dans le formulaire.
export const DEFAULT_MESSAGING_HOUSE = `Chaque ligne : Problem (la crainte ou le besoin implicite du client) | Feature | Benefit.

- Problem: their idea can get translated into the wrong technical solution before development even starts | Feature: Business & product discovery | Benefit: get software designed around what your business actually needs
- Problem: they build a complete first version before discovering which of their assumptions were right or wrong | Feature: Beta-user validation during development | Benefit: make more product decisions with real user feedback and fewer on assumptions
- Problem: they end up managing technical decisions they hired an expert to make | Feature: Direct senior developer access | Benefit: spend less time managing development and translating needs into technical decisions
- Problem: they are told what is happening rather than seeing the product take shape | Feature: Live cloud development environment | Benefit: see real progress without waiting for a status update or a deployment
- Problem: problems can reach users before the founder has properly reviewed them | Feature: Dedicated staging environment | Benefit: catch problems and misunderstandings before your users do
- Problem: they become dependent on a developer for routine operational changes | Feature: Fully-equipped admin dashboard | Benefit: make routine changes yourself without waiting for or paying a developer
- Problem: every post-launch learning can turn into another scope, quote and development bill | Feature: 3 months of revisions and improvements included from the first beta user | Benefit: improve the product with real user feedback without every change becoming another bill
- Problem: they can become locked into a developer they no longer want to work with | Feature: 100% code and infrastructure ownership | Benefit: change developers without losing access to or rebuilding your product
- Problem: replacing the original developer can mean weeks figuring out how the product works | Feature: standard tech stack plus a living technical knowledge base | Benefit: hand the product to another developer without starting from scratch
- Problem: a non-technical founder cannot easily judge code quality or technical debt | Feature: independent code audit welcome at any time | Benefit: verify the quality of the code without having to take Thibault's word for it
- Problem: they commit to a project without knowing what they will ultimately pay | Feature: fixed project pricing agreed before development starts | Benefit: avoid an open-ended development bill
- Problem: they have to commit thousands to someone they have never worked with | Feature: a paid 500€, two-day scoping step before any development commitment, fully deducted from the first milestone if we continue within 30 days | Benefit: validate the plan and the fit for a small, capped cost before committing to the full project`;

export const DEFAULT_TITLE_SLUG_PROMPT = `Tu génères le titre et le permalien d'une page de proposition commerciale, à partir du brief d'une demande publiée sur une plateforme de freelancing (Codeur, Malt, etc.).

Le titre :
- En français, court (60 à 75 caractères)
- Reformule le besoin du client de façon claire, concrète et engageante, en
  nommant un élément spécifique du brief (le type de produit, le secteur, la
  fonctionnalité clé) : jamais une simple reformulation de surface de la
  première phrase de l'annonce elle-même.
- Pas de nom d'agence, pas de "Proposition pour", pas de guillemets ni d'emoji
- Aucune formule creuse ("solution innovante", "plateforme sur mesure", "outil
  clé en main") : un titre qui irait pour n'importe quelle autre demande du
  même secteur est à recommencer.

Exemples de calibrage (le registre à viser, jamais à recopier) :
- "Portail immobilier avec recherche cartographiée et espace agence"
- "Plateforme de mise en relation entre coachs sportifs et clients"

Le permalien :
- Dérivé du titre, en minuscules, mots séparés par des tirets
- 3 à 6 mots significatifs, sans mots vides (le, la, les, de, des, pour, un, une...)
- Uniquement des lettres a-z non accentuées, des chiffres et des tirets

Brief de la demande :
${BRIEF_PLACEHOLDER}

${COMMON_AI_RULES}

Réponds UNIQUEMENT avec un objet JSON, sans texte autour :
{"title": "...", "slug": "..."}`;

/** Prompt d'UN écran : contexte partagé + focus de l'écran. Envoyé à generate_screen_from_text. */
export function buildScreenPrompt(
  sharedContext: string,
  screen: { title: string; deviceType: string; focus: string }
): string {
  const device =
    screen.deviceType === "MOBILE"
      ? "mobile"
      : screen.deviceType === "TABLET"
        ? "tablette"
        : "desktop";
  return `${sharedContext.trim()}

--- ÉCRAN À GÉNÉRER (un seul) ---
Titre : ${screen.title}
Format : ${device}
${screen.focus.trim()}

RAPPEL AVANT DE GÉNÉRER (le plus souvent oublié d'un écran à l'autre) : ce
n'est pas un nouvel écran isolé, c'est le MÊME LOGICIEL que les autres écrans
déjà générés pour ce projet. Reprends très exactement le header, la barre de
navigation, le logo ET LE BAS DE LA SIDEBAR (bloc quota, lien d'aide,
copyright, s'il y en a un) décrits dans le CAST COMMUN ci-dessus : même mise
en page du bandeau supérieur, même sidebar ou barre d'onglets (couleur de
fond, largeur, style de l'item actif), même logo (forme, couleur, position),
même bas de sidebar (présence ET contenu exact, pas juste un bloc similaire).
Si tu hésites sur une valeur de couleur, de mise en page ou de texte pour ce
chrome, ne réinvente rien : reprends la valeur déjà donnée dans le CAST
COMMUN, au pixel près. Seul le CONTENU de la zone principale change d'un
écran à l'autre.

Génère UNIQUEMENT cet écran, cohérent avec le contexte ci-dessus.`;
}

export const DEFAULT_PRICING_PROMPT = `Tu établis une grille tarifaire de prestation, fonctionnalité par fonctionnalité, à partir du brief d'une demande client publiée sur une plateforme de freelancing.

Brief de la demande :
${BRIEF_PLACEHOLDER}

Enjeux stratégiques déjà identifiés pour ce projet (si disponibles) : le
découpage base / complément et les priorités de chiffrage doivent y coller.
${ENJEUX_PLACEHOLDER}

Procède ainsi :

1. EXTRACTION
   Liste chaque fonctionnalité / lot de travail identifiable dans le brief.
   Regroupe ce qui va naturellement ensemble ; vise 5 à 9 lignes de base,
   pas une ligne par micro-détail.

2. BASE vs COMPLÉMENT ACTIVABLE
   - "base" : le périmètre nécessaire pour livrer le produit demandé
     (le cœur du besoin exprimé).
   - "addon" : ce qui est optionnel, "nice to have", ou explicitement
     présenté comme une amélioration / une phase 2 (ex : app mobile
     compagnon, scoring IA au-delà des filtres, gamification, montée en
     charge au-delà du lancement, intégrations secondaires).
   En cas de doute, mets en "base".

3. CHIFFRAGE (un montant fixe en euros par ligne, aucune TVA à ajouter :
   voir point 6)
   Chaque ligne a UN montant fixe, "amount", jamais une fourchette : c'est le
   prix que le porteur de projet paiera pour cette ligne.
   - Si le brief mentionne un budget ou une fourchette, cale la somme des
     montants des lignes "base" dessus.
   - Sinon, chiffre de façon réaliste pour une prestation de développement
     web sur mesure (React/Next.js + back Node/Python), en tenant compte de la
     complexité relative de chaque ligne. Contrainte dure, jamais dépassée : la
     somme des montants des lignes "base" reste TOUJOURS entre 3 000 et
     7 500 €, quelle que soit la complexité perçue du brief. Un brief plus
     large ou plus complexe se traduit par la RÉPARTITION entre les lignes
     (plus de lignes, une part plus grande vers les lignes lourdes) et par des
     montants individuels plus écartés entre lignes simples et lignes lourdes,
     jamais par un total qui dépasse ce plafond.
   - Montants ronds (centaines d'euros). Les lignes plus lourdes coûtent
     nettement plus que les lignes simples : ne mets pas le même prix partout.
   - Vérifie avant de continuer : additionne les montants de toutes les lignes
     "base". Si le total dépasse 7 500 € (et qu'aucun budget n'était mentionné
     dans le brief, seul cas où ce plafond ne s'applique pas), tu as
     surestimé. Corrige avant de répondre, par ordre de préférence : (1)
     réduis les montants des lignes les plus chères, (2) déplace en "addon"
     les lignes les moins essentielles au besoin exprimé.
   - Les lignes "addon" ont aussi un montant fixe, chiffré indépendamment.

4. ESTIMATION EN JOURS (un nombre de jours par ligne, budget d'abord, puis
   répartition)
   Chaque ligne a aussi UN nombre de jours, "days". L'exécutant est un
   développeur senior full-stack très rapide, outillé (assistants IA, starters
   et composants maison déjà prêts). On compte le temps réel que LUI
   passerait, pas un standard d'agence.

   a. Fixe d'abord le TOTAL de jours "base" du projet :
      - projet standard (portail + espace pro + quelques intégrations) :
        15 à 22 jours
      - projet plus large : 22 à 30 jours
      - ne dépasse 30 que si le brief décrit vraiment un périmètre
        exceptionnel, et justifie-le.
   b. Répartis ensuite ce total entre les lignes "base". Repères par ligne :
      - ligne simple (CRUD, page, formulaire) : 0.5 j
      - ligne moyenne (recherche multicritère, tableau de bord, une
        intégration) : 1 à 2 j
      - ligne lourde (matching, sync temps réel, paiement récurrent
        multi-niveaux) : 2 à 4 j
   c. Vérifie : si la somme des jours "base" dépasse ton total de l'étape a,
      tu as surestimé. Réduis chaque ligne jusqu'à retomber dessus.

   Valeurs autorisées : 0.5, 1, 1.5, 2, 3, 4, 5. Les lignes "addon" suivent
   les mêmes repères. Garde la cohérence jours / montant (ordre de grandeur :
   175 à 225 € par jour).

5. JALONS
   Range les lignes "base" dans 2 à 4 jalons, dans l'ordre où le projet sera
   livré : chaque jalon se termine par quelque chose que le porteur de projet
   peut tester ou utiliser. Donne à chaque jalon un titre court (2 à 5 mots)
   qui dit ce qu'il livre, pas une phase technique (pas "Backend" ni
   "Intégration", mais "Les comptes et l'espace client" ou "Le paiement et la
   mise en ligne"). Toutes les lignes "base" d'un même jalon portent
   exactement le même titre dans "milestone". Le montant d'un jalon est la
   somme de ses lignes : équilibre les jalons pour qu'aucun ne dépasse la
   moitié du total. Les lignes "addon" n'ont pas de jalon ("milestone": null).

6. TVA
   Je ne facture pas de TVA (franchise en base) : n'écris jamais "HT", "TVA en
   sus" ni aucune formule qui laisse penser qu'un montant s'ajoutera au
   total affiché. Les montants indiqués sont définitifs.

7. LANGUE ET DESCRIPTIONS
   Tout en français. Montants en euros entiers (pas de centimes, pas de
   symbole dans le nombre). Pour "description", pars du résultat obtenu par
   le porteur de projet, pas de la liste des tâches techniques : pas
   "Développement d'un formulaire multi-étapes avec validation", mais ce que
   ça permet une fois en place ("Vos visiteurs déposent une demande complète
   sans appel ni relance"). Avant de valider une description, applique le
   test "et alors ?" : si la phrase décrit une fonctionnalité sans dire ce
   qu'elle change concrètement pour le porteur de projet ou ses visiteurs,
   ajoute cette conséquence ou reformule.
${COMMON_AI_RULES}

Réponds UNIQUEMENT avec un objet JSON, sans texte autour, sans bloc markdown :
{
  "briefBudgetMentioned": true ou false — true UNIQUEMENT si le brief cite
    lui-même un budget ou une fourchette chiffrée (le seul cas où le plafond
    de l'étape 3 ne s'applique pas) ; false dans tous les autres cas, y
    compris pour un projet perçu comme complexe.
  "lines": [
    {
      "label": "Nom court de la ligne (3 à 8 mots)",
      "description": "1 à 2 phrases : le résultat concret pour le porteur de projet, pas la tâche technique.",
      "kind": "base" ou "addon",
      "milestone": "Titre du jalon (lignes base) ou null (lignes addon)",
      "amount": 2200,
      "days": 4
    }
  ],
  "vatNote": "Mention à afficher sous le total, sans jamais évoquer une TVA à ajouter (ex : \\"Montants nets, sans TVA applicable.\\")"
}`;

export const DEFAULT_STAKES_PROMPT = `Tu formalises les ENJEUX STRATÉGIQUES d'un projet, à partir du brief d'une
demande publiée sur une plateforme de freelancing. Cette formalisation sert
deux usages : elle est réinjectée dans la génération des maquettes, du chiffrage
et de l'étude de cas pour qu'ils restent cohérents, et elle est affichée telle
quelle sur la page de proposition, lue par le porteur du projet.

Brief de la demande :
${BRIEF_PLACEHOLDER}

Produis 2 à 3 enjeux (jamais plus de 3), ordonnés du plus structurant (ce qui
conditionne l'architecture technique) au plus superficiel. Chaque enjeu doit
couvrir une dimension du projet clairement distincte des autres : si deux
enjeux se recoupent, fusionne-les ou remplace le second par un angle
vraiment différent. Chaque enjeu est une carte avec 5 champs :

- category : le domaine ou la fonction concernée (ex : "Recherche", "Annonces",
  "Monétisation", "Back-office"). Un ou deux mots. Reprends le vocabulaire du
  brief.
- enjeu : le titre de la carte. Le résultat concret attendu, formulé comme un
  livrable ("La recherche de biens filtrée et cartographiée"), pas comme un
  objectif vague ("Améliorer la recherche").
- probleme (rendu sous le libellé "Ce que je regarderais") : ce que ce point
  soulève à mes yeux ET pourquoi ça compte pour la suite du projet - pas une
  affirmation tranchée sur comment le brief FONCTIONNE (vous connaissez votre
  métier mieux que moi), mais ce à quoi je ferais attention si j'étais à votre
  place, et l'enjeu concret que ça représente (volume et fraîcheur des
  données, performance à grande échelle, fiabilité d'un import externe,
  gestion des droits et des rôles, montée en charge, cohérence entre
  plusieurs sources, cas limites...). Une à deux phrases, ancrées sur ce que
  vous décrivez, jamais présentées comme un fait acquis sur votre activité.
- solution (rendu sous le libellé "Comment je m'y prendrais") : l'approche
  technique que je retiendrais pour répondre à ce point, présentée comme MA
  proposition de départ, pas comme la seule façon de faire. Une à deux
  phrases concrètes.
- resultat : une phrase qui décrit ce qui deviendra possible une fois le produit
  livré. Reprends la terminologie du brief pour nommer qui agit (le visiteur,
  l'agence, le gestionnaire, le poseur...), n'emploie jamais le mot générique
  "utilisateur". Phrase complète, pas de titre, pas de verbe à l'infinitif en
  ouverture.

Règles :
- Adresse-toi au porteur du projet à la deuxième personne ("Vous demandez...",
  "Vous voulez...", "Votre portail doit..."). N'emploie jamais "le client" ni
  "votre client" pour le désigner, et ne dis jamais "le brief" : c'est vous qui
  parlez, pas un document.
- Ne présente jamais une hypothèse tirée du brief comme une certitude sur le
  métier ou le produit du porteur de projet : c'est un développeur qui pense
  tout haut à partir d'une annonce, pas quelqu'un qui a déjà tout compris de
  l'activité. "probleme" et "solution" restent MON point de vue de départ, pas
  un diagnostic définitif.
- Uniquement ce qui est étayé par ce que vous décrivez. Aucune supposition sur
  le budget, le marché ou les concurrents s'ils ne sont pas mentionnés. Ne
  reproche pas un manque de précision ("sans préciser l'existant") : travaille
  avec ce qui est là. Si l'information est trop mince pour un vrai point de
  tension, fais moins de cartes.
- Ton neutre et factuel. Pas de superlatifs, pas de langage commercial.

${buildAntiSlopCheck('"probleme" et "resultat" de chaque carte')}

${COMMON_AI_RULES}

Réponds UNIQUEMENT avec un objet JSON, sans texte autour, sans bloc markdown :
{
  "items": [
    {
      "category": "...",
      "enjeu": "...",
      "probleme": "...",
      "solution": "...",
      "resultat": "..."
    }
  ]
}`;

// Plan des maquettes "une par enjeu" : recherche secteur, données réalistes,
// direction artistique, designSystem + sharedContext, avec un mapping "un
// écran par enjeu illustrant sa solution", dans l'ordre des enjeux.
export const DEFAULT_STAKES_MOCKUPS_PROMPT = `Tu traduis les ENJEUX STRATÉGIQUES d'un projet en une série de maquettes UI pour
Stitch (Google Labs) : UN écran par enjeu, qui montre concrètement la solution
décrite, avec de vraies données du secteur, jamais un schéma abstrait.

NATURE DE CHAQUE ÉCRAN (règle absolue) : c'est la CAPTURE D'ÉCRAN d'une
application web utilisée par un professionnel : barre de navigation ou menu
latéral, en-tête avec logo et compte connecté, contenu structuré en tableaux,
listes, cartes de données, filtres, boutons d'action, champs de formulaire.
JAMAIS une photographie plein cadre, JAMAIS une page d'accueil marketing, JAMAIS
une image d'ambiance (intérieur, paysage, produit). Les photos éventuelles
n'apparaissent que sous forme de petites vignettes DANS des cartes ou des
lignes de liste, jamais en fond ni en pleine largeur.

Demande initiale du projet :
${BRIEF_PLACEHOLDER}

Enjeux déjà formalisés, un bloc par enjeu, DANS L'ORDRE (tu produiras un écran
par enjeu, dans ce même ordre) :
${ENJEUX_PLACEHOLDER}

Procède dans cet ordre :

1. IDENTIFICATION DU SECTEUR ET DE SES RÉFÉRENCES RÉELLES
   Déduis le secteur d'activité précis et son positionnement (pas juste
   "immobilier" : agence de quartier, portail grand public, prestige...).
   Identifie 2-3 acteurs réels représentatifs. Recherche leurs conventions
   concrètes : format des références, vocabulaire et champs d'une fiche,
   structure des filtres, unités et échelles (ex. DPE de A à G), format des
   adresses. Ce sont des références de STRUCTURE et de VOCABULAIRE, pas de style.

2. UN ÉCRAN PAR ENJEU
   Pour chaque enjeu, dans l'ordre, définis l'écran unique qui rend visible sa
   SOLUTION. L'écran doit contenir de vrais éléments d'interface (listes
   peuplées, cartes, tableaux, filtres, boutons d'action), pas un wireframe vide.
   INTERDIT : l'écran ne doit reprendre NULLE PART, ni en titre ni en sous-titre
   ni en bandeau, le texte de l'enjeu, sa problématique ou sa solution mot pour
   mot. C'est une vraie page produit, avec ses propres libellés fonctionnels
   ("Passerelles logicielles", "Tableau des flux", "Fiche partenaire"...), pas
   une slide qui cite l'argumentaire.

3. DIRECTION ARTISTIQUE
   Déduis une direction visuelle distinctive et cohérente avec le secteur,
   jamais une esthétique par défaut de générateur IA (dégradés violet/bleu,
   glassmorphism, cartes flottantes, icônes stock, arrondis uniformes, typo
   Inter/Poppins). S'ajoute à cette liste un combo tout aussi reconnaissable
   et à éviter par défaut : fond crème/beige, accent vert foncé et titres en
   police serif "institutionnelle" (le réflexe "outil sérieux, de confiance")
   — sauf si un vrai raisonnement sectoriel y mène (ex : un secteur où le
   papier/le notarial est la référence visuelle assumée), jamais comme choix
   par défaut faute d'idée.
   - Motif signature : choisis UN motif structurel qui revient sur tous les
     écrans (ex : rails verticaux qui débordent légèrement du cadre, bordure
     en dégradé fine avec un intérieur plus sombre, encoches d'angle) : c'est
     lui qui rend la direction reconnaissable, pas seulement la couleur. Ne
     mélange jamais plusieurs partis pris matériels sur un même écran
     (jamais glassmorphisme + skeuomorphisme + cadres ensemble).
   - Contraste typographique : headlineFont et bodyFont doivent contraster de
     catégorie (une serif avec une sans-serif, une géométrique avec une
     humaniste), jamais deux polices sans-serif presque identiques. Justifie
     ce choix en une phrase dans designMd.
   - Structure justifiée par l'usage : la structure de navigation (menu
     latéral, onglets, fil d'ariane) doit se justifier par l'usage réel
     décrit dans le brief, jamais par défaut. Un configurateur pas à pas, une
     messagerie ou une timeline peuvent mieux correspondre qu'un tableau de
     bord classique selon le produit.
   - Aucun panneau ne recouvre jamais le contenu principal : un panneau
     latéral ("Enregistrer un paiement", un formulaire de détail, un tiroir
     d'édition...) partage l'écran en colonnes CÔTE À CÔTE, chacune avec sa
     propre largeur, jamais en glissant PAR-DESSUS le tableau ou les cartes
     déjà affichés. Si un écran doit montrer à la fois une liste/un tableau ET
     un panneau de détail ou de saisie, prévois explicitement les deux zones
     dans le focus de cet écran (par exemple 2/3 de la largeur pour la liste,
     1/3 pour le panneau) plutôt que de laisser le panneau chevaucher et
     couper des colonnes du tableau.
   - Couleurs de statut, VRAIMENT distinctes de l'accent de marque : si le
     secteur implique des statuts visuels (priorité, niveau de stock, état de
     publication, paiement reçu/en retard...), fixe 2 à 3 couleurs sémantiques
     (succès / alerte / danger) d'une TEINTE différente de l'accent de marque,
     pas seulement une nuance plus claire ou plus foncée de la même teinte. Si
     l'accent de marque est déjà vert, la couleur de succès ne peut pas être
     vert aussi : le statut positif doit se détacher visuellement de la
     marque, pas s'y fondre. Documente ces couleurs dans designMd.
   - Chiffres financiers ou quantitatifs : toute colonne de montants, de
     quantités ou de pourcentages dans un tableau est alignée à droite avec
     des chiffres tabulaires (largeur fixe), jamais alignée à gauche mélangée
     au texte : c'est ce qui permet de comparer une colonne de chiffres d'un
     coup d'œil.

4. CAST COMMUN (cohérence entre écrans)
   Fige un jeu d'entités concrètes RÉUTILISÉ À L'IDENTIQUE sur tous les écrans,
   et écris-le dans sharedContext. Au minimum :
   - L'utilisateur connecté : un nom complet, un rôle, une structure. Le MÊME
     partout (jamais un nom sur un écran et un autre nom sur le suivant).
   - L'organisation principale : nom, ville et quartier précis, chiffres clés
     (nombre d'annonces, forfait, quota...). Les MÊMES partout.
   - 5 à 8 entités secondaires nommées (agences partenaires, biens, flux...)
     avec leurs attributs figés (réf, prix, surface, ville). Chaque écran pioche
     dans cette liste, aucun écran n'invente une entité hors liste.
   - Les formats : référence d'annonce, adresse, montant, date.
   - Les LIBELLÉS EXACTS de la structure de navigation elle-même quand l'écran
     en a une (noms des colonnes d'un tableau Kanban, intitulés des onglets
     de réglages, items d'un menu admin) : ce ne sont pas des données à
     varier d'un écran à l'autre, mais des labels fixes au même titre que le
     reste du CAST COMMUN.
   - LE HEADER ET LA NAVIGATION, en valeurs EXACTES et réutilisables, pas en
     adjectifs vagues ("sombre", "épuré") qui laissent trop de latitude d'un
     écran à l'autre. Fige et écris dans sharedContext, avec des valeurs
     concrètes que chaque écran devra recopier telles quelles :
     - Structure : barre latérale ou barre supérieure (une seule des deux,
       jamais les deux styles mélangés selon l'écran), sa largeur ou hauteur
       approximative, sa couleur de fond exacte (nom de couleur ou hex, cohérent
       avec designSystem).
     - Logo : sa forme précise (ex : icône de maison en traits fins dans un
       carré arrondi + texte à côté), sa couleur, sa position, IDENTIQUE sur
       tous les écrans qui ont un header.
     - Onglet ou item actif : sa couleur de fond exacte ou son style de
       soulignement, le même partout.
     - BAS DE LA SIDEBAR OU DU HEADER, s'il y en a un (bloc quota/forfait,
       lien d'aide, mention de copyright...) : décide UNE FOIS pour toutes
       s'il existe et ce qu'il contient EXACTEMENT (même texte, même mise en
       page), puis reproduis-le à l'identique sur CHAQUE écran qui a cette
       sidebar. Ce bloc ne doit ni apparaître sur un écran et disparaître sur
       un autre, ni changer de contenu (un indicateur de quota présent ici
       mais absent là, un lien "Aide" à un endroit et "Assistance juridique"
       à un autre) : c'est aussi figé que le reste du chrome.
     Un logo qui change de forme, une sidebar qui change de couleur ou de
     largeur, un item actif stylé différemment, ou un bas de sidebar qui
     change de contenu d'un écran à l'autre cassent l'illusion d'une seule et
     même application, même si tous les LIBELLÉS de navigation principaux sont
     par ailleurs identiques.
   Valeurs cohérentes avec les références réelles de l'étape 1, jamais de
   placeholders génériques ("Lorem", "123 Main St"). Volume réaliste (une liste
   a 5 à 10 lignes, pas 1).

5. ÉTATS, IMAGES ET COPIE
   Ajoute les états vide / en attente pertinents. Les visuels (photos de biens,
   avatars) restent des VIGNETTES à l'intérieur de cartes ou de lignes de liste,
   petites, jamais un visuel qui occupe l'écran. Le focus doit décrire l'écran
   comme une interface ("tableau de bord", "liste filtrable", "fiche avec
   onglets"...), jamais comme une image.
   - Vocabulaire de composants précis : nomme les éléments avec des termes
     reconnus (carte KPI avec mini-graphique, tiroir latéral, fil d'ariane,
     barre d'onglets), pas seulement le type d'écran général.
   - État actif obligatoire : indique l'état actif ou sélectionné d'au moins
     un élément par écran (onglet actif surligné, étape en cours dans un
     parcours, interrupteur activé, badge "le plus demandé"). Ce n'est pas
     optionnel : la quasi-totalité des écrans réels en montre un.
   - Copie avec une vraie voix : donne le texte exact d'au moins un élément
     visible qui n'est pas une simple donnée (un titre d'accroche, un message
     d'état vide, une question d'étape) : ce texte doit avoir une personnalité
     propre au secteur, jamais une étiquette neutre du type "Aucun résultat."

6. LANGUE, DATES, PONCTUATION (règle absolue, à recopier intégralement dans sharedContext)
   TOUT le texte visible est en FRANÇAIS : titres, labels, boutons, menus,
   placeholders, messages d'état, colonnes, mentions légales, footer. Aucun mot
   en anglais. Dates au format français ("12 mars ${YEAR_PLACEHOLDER}"), monnaie
   en euros avec séparateur français ("450 000 €").
   ANNÉE : chaque année visible vaut EXACTEMENT ${YEAR_PLACEHOLDER}, footer et
   copyright compris ("© ${YEAR_PLACEHOLDER}"), jamais 2023 / 2024 / 2025 ni une
   autre année.
   PONCTUATION : aucun tiret cadratin ni demi-cadratin (— ou –), nulle part, ni
   dans les titres ni dans les libellés. Utilise deux-points, virgule,
   parenthèses ou un simple tiret court entouré d'espaces si besoin.

7. DEVICE
   deviceType par écran selon l'usage réel : DESKTOP pour un back-office ou une
   recherche riche, MOBILE pour une action terrain, TABLET si pertinent.

Réponds UNIQUEMENT avec un objet JSON (sans texte autour, sans bloc markdown) de
cette forme exacte :
{
  "designSystem": {
    "displayName": "Nom court du système de design",
    "colorMode": "LIGHT" ou "DARK",
    "customColor": "#RRGGBB (couleur d'accent principale, cohérente avec l'étape 3)",
    "headlineFont": "un nom de la liste FONTS ci-dessous",
    "bodyFont": "un nom de la liste FONTS ci-dessous",
    "roundness": "ROUND_FOUR" | "ROUND_EIGHT" | "ROUND_TWELVE" | "ROUND_FULL",
    "designMd": "Guidelines de design en markdown : palette (hex, y compris les 2-3 couleurs de statut de l'étape 3 si le secteur en a besoin), usage des couleurs, échelle typographique, motif signature (étape 3), justification du contraste headline/body, densité, style des composants (boutons, cartes, champs), iconographie, ton. En français."
  },
  "sharedContext": "Le contexte PARTAGÉ, en français, préfixé à CHAQUE génération d'écran. Sections :\\n- Contexte général (produit, secteur, positionnement)\\n- Références structurelles réelles (étape 1 : formats, vocabulaire, unités)\\n- Direction visuelle (étape 3, alignée sur designSystem, motif signature, couleurs de statut distinctes de l'accent de marque, et alignement des chiffres compris)\\n- CAST COMMUN (étape 4) : l'utilisateur connecté, l'organisation principale, 5 à 8 entités secondaires, les libellés exacts de navigation, ET le traitement visuel figé du header (logo, mise en page du bandeau, style de l'onglet actif), tous NOMMÉS avec leurs attributs figés. Consigne : chaque écran n'utilise QUE ces entités, à l'identique, et n'en invente aucune.\\n- Règle langue et dates (étape 6, reprise intégralement)\\n- À éviter explicitement\\nNE décris PAS les écrans un par un ici.",
  "screens": [
    {
      "title": "Nom fonctionnel court et neutre de l'écran, JAMAIS l'intitulé de l'enjeu ni une phrase marketing (ex: Passerelles logicielles, Tableau des flux, Fiche partenaire)",
      "deviceType": "DESKTOP" | "MOBILE" | "TABLET",
      "focus": "2 à 4 phrases : ce que CET écran montre pour illustrer la solution de l'enjeu, avec un vocabulaire de composants précis (carte KPI, tiroir latéral, fil d'ariane...), l'élément en état actif/sélectionné, et le texte exact d'au moins une copie visible avec une vraie voix. N'y recopie pas l'intitulé de l'enjeu. N'utilise que des entités et des libellés de navigation du CAST COMMUN de sharedContext, aux mêmes valeurs (même nom d'utilisateur, même ville, mêmes chiffres d'un écran à l'autre)."
    }
  ]
}

Il doit y avoir EXACTEMENT autant d'écrans que d'enjeux, dans le même ordre.

FONTS autorisées (choisis-en qui servent la direction artistique, évite INTER par défaut) : BE_VIETNAM_PRO, EPILOGUE, LEXEND, MANROPE, NEWSREADER, NOTO_SERIF, PLUS_JAKARTA_SANS, PUBLIC_SANS, SPACE_GROTESK, SPLINE_SANS, WORK_SANS, DOMINE, LIBRE_CASLON_TEXT, EB_GARAMOND, LITERATA, SOURCE_SERIF_FOUR, MONTSERRAT, SOURCE_SANS_THREE, NUNITO_SANS, HANKEN_GROTESK, RUBIK, GEIST, DM_SANS, IBM_PLEX_SANS, SORA, INTER.`;

export const DEFAULT_MESSAGES_PROMPT = `Tu rédiges les deux messages d'accompagnement d'une proposition commerciale,
à partir du brief d'un client publié sur une plateforme de freelancing.
Ces deux messages ont des rôles différents et ne doivent jamais se répéter :

- LE TEASER : publié directement sur la plateforme (Codeur.com, Malt...),
  c'est le premier message que le client lit. Approche commerciale DIRECTE
  (depuis le 2026-10-12) : en trois phrases au plus, il montre que le projet
  a été compris en profondeur, puis il expose point par point, en liste, ce
  que Léman IT apporte de concret à CE projet, avec des preuves. Il doit
  convaincre à lui seul ; la page de proposition donne ensuite le détail.
- LE MOT DE PAGE : affiché en haut de la page de proposition, lu juste
  après avoir cliqué sur le lien du teaser. Il annonce ce que la page va
  couvrir et porte la motivation/l'engagement de l'auteur.

Brief de la demande :
${BRIEF_PLACEHOLDER}

URL de la page de proposition :
${URL_PROPOSITION_PLACEHOLDER}

Sections réellement présentes sur la page de proposition (n'annonce QUE
celles listées ici, dans le mot de page comme dans le teaser) :
${SECTIONS_DISPONIBLES_PLACEHOLDER}

BANQUE DE VALEUR (seule source autorisée pour tout engagement, garantie,
méthode de travail ou capacité technique mentionnée dans les deux messages) :
${MESSAGING_HOUSE_PLACEHOLDER}

Prix et délai calculés par le système de chiffrage (seule source autorisée
pour tout prix ou délai cité dans le TEASER ; n'apparaît jamais dans le mot
de page). Un montant unique est un PRIX FIXE ; deux montants séparés par un
tiret sont une fourchette (offre chiffrée avant le passage au prix fixe) :
- Prix : ${PRICE_RANGE_PLACEHOLDER}
- Délai : ${DELAY_RANGE_PLACEHOLDER}
Si l'une de ces deux lignes affiche "(fourchette non disponible)", la grille
tarifaire n'est pas encore prête : n'aborde ni prix ni délai dans le teaser,
saute directement au lien. Ne calcule, n'arrondis ni n'estime jamais toi-même
un prix ou un délai : reprends ces chiffres tels quels, dans une phrase à toi.

RÈGLE DE SÉLECTION DANS LA BANQUE DE VALEUR :
1. Repère dans le brief les signaux qui indiquent une inquiétude ou un
   besoin implicite du client (ex : "je cherche un collaborateur, pas un
   prestataire" indique un signal de confiance/relation long terme, pas
   juste de compétence technique).
2. Dans la banque de valeur, identifie la ligne dont la colonne "Problem"
   correspond le plus précisément à ce signal, pas la ligne la plus
   impressionnante, celle qui répond vraiment à ce que le client craint.
3. TEASER : retiens 3 à 4 lignes de la banque, celles qui répondent le
   mieux aux risques et aux attentes de CE projet, classées de la plus
   décisive pour ce client à la moins décisive. MOT DE PAGE : 1 élément au
   plus, différent de ceux du teaser. Le reste de la banque sert de
   garde-fou (ce que tu as le droit de dire), pas de contenu à écouler.
4. Traduis et reformule l'élément choisi en français dans la voix de
   référence ci-dessous, ne recopie jamais le libellé anglais tel quel.
5. Chaque avantage retenu doit pouvoir se relier à un élément réel du
   brief. Si une ligne ne se relie à rien de ce projet, ne la retiens pas.

VOIX DE RÉFÉRENCE : reproduis ces paramètres de style dans les deux messages,
jamais le contenu ou le contexte de l'exemple qui les illustre ci-dessous.
- Longueur de phrase : courtes à moyennes, jamais de phrase à propositions
  multiples empilées par des virgules.
- Absence d'effet de style : pas de métaphore, pas d'image, pas de tournure
  orale forcée ("franchement", "du coup"), pas d'humour construit.
- Chaleur par le FOND, jamais par la forme : la chaleur vient d'un vrai
  compliment précis ou d'un engagement concret, jamais d'un ton enjoué ou
  d'une ponctuation expressive (pas de point d'exclamation).
- Voix active à la première personne ("je" sujet de la plupart des phrases),
  jamais de sujet abstrait en ouverture de phrase.
- Ton presque neutre, factuel, mais jamais froid : chaque phrase apporte une
  information ou un engagement réel, aucune formule de politesse creuse.
- Signature : juste le prénom, rien d'autre.

Exemple réel écrit par l'auteur, qui illustre CES paramètres de style
uniquement : ne recopie ni son ouverture, ni son propos (transmission,
associé développeur), ni sa liste de garanties (maintenance, hébergement,
prix) dans les messages que tu rédiges, ils sont propres à un autre projet.
Sa question finale n'est plus la règle : les messages finissent désormais
sur une affirmation. Sers-t'en pour calibrer le ton et la liste à puces,
jamais comme gabarit de contenu :

"Bonjour,
Votre projet me semble vraiment intéressant et comble un besoin de
transmission qui n'existe pas sur le marché actuellement. J'ai vu que vous
étiez à la recherche d'un associé développeur. Je sais qu'il est parfois
difficile pour les porteurs de projets de trouver l'associé technique qui
leur correspond, qui est fiable et engagé dans le projet sur une longue
durée. C'est pourquoi je propose d'accompagner les entrepreneurs sur la
partie développement du projet.
- Je vous aide à clarifier votre cahier des charges techniques et à créer
  des maquettes fonctionnelles
- Je code le projet de A à Z avec vos retours réguliers
- Je garantis la maintenance du code gratuitement pendant 3 mois
- J'offre l'hébergement et sa maintenance pendant 1 an gratuitement.
- Un prix très attractif et adapté aux entrepreneurs en phase de création
Je vous propose que nous échangions à ce sujet la semaine prochaine ?
Thibault"

INTERDITS ABSOLUS (identiques pour les deux messages) :
- Aucun chiffre d'expérience, durée de pratique, nombre d'années ou
  statistique professionnelle inventée. Utilise EXCLUSIVEMENT ce qui figure
  dans la banque de valeur ci-dessus.
- N'annonce jamais un contenu (section, exemple, référence) qui n'apparaît
  pas explicitement dans {{sections_disponibles}}.
- Aucun humour construit, aucune faute d'orthographe volontaire.
- N'ouvre JAMAIS sur une présentation biographique ou générique, du type
  "Bonjour, je suis développeur full-stack", "Fort de plusieurs années
  d'expérience", "Votre projet a immédiatement retenu mon attention", "Je
  serais ravi de...", "Je suis spécialisé dans...", "Je peux vous aider à
  construire...". Si l'ouverture pourrait être envoyée telle quelle à 20
  autres projets sans rien changer, ce n'est pas une ouverture valable :
  reviens à un détail réel et spécifique de CE brief.
- Ne récite jamais la liste de fonctionnalités déjà énumérées dans le brief
  (ex : "Je peux développer la recherche, la carte interactive, les
  annonces, l'espace agence, les abonnements et le back-office") : le client
  sait déjà que ces éléments sont attendus, les lui répéter n'apporte rien.
${COMMON_AI_RULES}

Procède dans cet ordre :

1. OBSERVATION D'OUVERTURE (pour LE TEASER uniquement)
   Identifie dans le brief UNE observation réelle qui montre un vrai jugement
   technique ou produit sur CE projet précis, jamais une reformulation de ce
   que le client a déjà écrit : remettre en question une évidence apparente,
   repérer un problème caché que le brief laisse deviner, ou nommer
   l'arbitrage dont dépend le projet. Elle doit pouvoir faire penser au
   client "je n'avais pas pensé à ça sous cet angle". Ne fabrique jamais un
   problème artificiel : si le brief est trop mince, formule-la comme le
   point qui décidera de la réussite du projet.

2. SÉLECTION DANS LA BANQUE DE VALEUR
   Applique la RÈGLE DE SÉLECTION ci-dessus (3 à 4 lignes pour le teaser,
   1 au plus pour le mot de page).

3. TEASER (approche directe, liste à puces)
   Structure, dans cet ordre :
   a. "Bonjour," seul sur la première ligne.
   b. OUVERTURE : 3 phrases au plus. Leur projet saisi par l'observation de
      l'étape 1, ancrée sur un détail précis du brief, puis ce dont dépend
      sa réussite. Pas de résumé du brief, pas de présentation de toi.
   c. LISTE de 4 à 6 puces (chaque ligne commence par "- "), sans phrase
      d'annonce avant ("voici ce que je propose", "concrètement :") : la
      dernière phrase de l'ouverture mène naturellement à la liste.
      - 3 à 4 puces AVANTAGE, une par élément retenu à l'étape 2 : le
        mécanisme concret de Léman IT (ex. "un prix fixe par jalon",
        "une version en ligne que vous testez pendant tout le
        développement"), puis, après deux-points, la différence que ça fait
        DANS CE PROJET, en nommant un élément du brief (un utilisateur, un
        écran, une contrainte, un délai). Une puce qui pourrait s'envoyer
        telle quelle à un autre projet est à réécrire.
      - 1 puce PREUVE si une étude de cas figure dans les sections
        disponibles : le nom du projet et son résultat réel, repris de la
        ligne "Étude de cas" sans rien ajouter ni arrondir (un témoignage
        peut être mentionné par le nom et le rôle de son auteur, jamais
        cité de mémoire). Sans étude de cas, pas de puce preuve.
      - 1 puce PRIX ET DÉLAI si le prix/délai est disponible (voir plus
        haut). Un montant unique : "prix fixe de ... pour ce périmètre,
        ... de développement". Une fourchette : une estimation ("autour
        de ..."). Reprends les chiffres tels quels. Si la ligne affiche
        "(fourchette non disponible)", pas de puce prix.
      Chaque puce tient en une ou deux lignes (35 mots au plus).
   d. LIEN : une phrase qui dit ce que le client trouve sur la page,
      choisie dans {{sections_disponibles}}, puis l'URL seule sur sa ligne :
      ${URL_PROPOSITION_PLACEHOLDER}
   e. CLÔTURE : une phrase AFFIRMATIVE qui propose un appel de 30 minutes,
      formulée à ta façon. Aucun point d'interrogation dans tout le teaser.
   f. "Thibault" seul sur la dernière ligne.
   - Ne révèle JAMAIS de détail technique de stack (langage, framework,
     base de données).
   - Longueur cible : 130 à 220 mots, signature comprise.

4. MOT DE PAGE (paragraphes, pas de liste à puces)
   Commence obligatoirement par "Bonjour," sur sa propre ligne, et termine
   obligatoirement par le prénom seul en signature.
   Ce message est lu immédiatement après le teaser : ne réutilise JAMAIS
   l'observation de l'étape 1 ni les avantages déjà listés dans le teaser.
   Structure :
   a. Phrase de transition supposant l'arrivée depuis le teaser, suivie
      d'une phrase qui exprime une vraie motivation ou un engagement de
      qualité, si possible ancrée sur un élément de la banque de valeur
      choisi à l'étape 2 (méthode de travail, garantie, façon de
      collaborer) plutôt qu'une affirmation vague.
   b. Une phrase en prose continue qui annonce ce que la page va couvrir,
      strictement limitée à {{sections_disponibles}}.
   c. Si le brief contient un détail réel distinct des observations déjà
      utilisées dans le teaser (ex : des éléments listés comme des lignes de
      chiffrage séparées alors qu'ils sont inclus par défaut), ajoute un
      point de clarification honnête dessus. Sinon, passe directement à la
      clôture.
   d. Clôture par une proposition d'échange concrète.

5. LANGUE
   Tout en français, y compris les éléments traduits depuis la banque de
   valeur.

6. ${buildAntiSlopCheck("le teaser et le mot de page")}
   Si le total est sous 35/50, l'ouverture ou les puces ne sont
   probablement pas assez ancrées dans CE projet : renforce-les avant de
   réécrire.

   VÉRIFICATION AVANT DE RÉPONDRE (spécifique au teaser) :
   - Cette ouverture pourrait-elle être envoyée telle quelle à 20 autres
     projets ? Si oui, réécris-la. Dépasse-t-elle 3 phrases ? Si oui,
     condense-la.
   - Chaque puce avantage nomme-t-elle un élément de CE projet ? Si non,
     réécris-la.
   - La preuve vient-elle mot pour mot des faits de l'étude de cas, sans
     chiffre ajouté ? Si non, corrige-la ou retire-la.
   - Le teaser contient-il un point d'interrogation ? Si oui, transforme
     la phrase en affirmation.
   - Le prix ou le délai cité (s'il y en a un) vient-il EXACTEMENT des
     chiffres fournis plus haut, sans arrondi ni invention ? Si non,
     corrige-le ou retire-le.

Réponds UNIQUEMENT avec un objet JSON, sans texte autour, sans bloc markdown :
{
  "detail_accroche": "L'observation d'ouverture de l'étape 1, résumée en 1 phrase.",
  "element_banque_utilise": "Les éléments de la banque de valeur retenus (Feature), séparés par des points-virgules, ou null.",
  "teaser": "Texte complet du teaser, prêt à coller sur la plateforme.",
  "mot_de_page": "Texte complet du mot de page, prêt à insérer en haut de la proposition."
}`;

export const DEFAULT_CASE_STUDY_PROMPT = `Tu choisis LA réalisation Léman IT la plus pertinente à montrer à un prospect,
à partir du brief de sa demande publiée sur une plateforme de freelancing, puis
tu rédiges les points communs entre cette réalisation et le projet du prospect.

Brief de la demande :
${BRIEF_PLACEHOLDER}

Enjeux stratégiques déjà identifiés pour ce projet (si disponibles) : le choix
de la réalisation doit coller à ces enjeux en priorité.
${ENJEUX_PLACEHOLDER}

RÉALISATIONS DISPONIBLES (seule source autorisée : tu ne peux choisir qu'un des
id ci-dessous, et tu ne cites que ce qui y figure) :
${REALISATIONS_PLACEHOLDER}

Procède ainsi :

1. RAPPROCHEMENT
   Repère ce qui, dans le brief, peut résonner avec une réalisation : secteur ou
   positionnement proche, même type de produit (SaaS, marketplace, dashboard,
   pipeline de données...), fonctionnalité comparable (assistant IA, RAG,
   gamification, recherche multicritère, automatisation...), même situation de
   départ (refonte d'un existant, fondateur non technique, délai ou budget
   contraint, réseau multi-sites...).

2. CHOIX
   Choisis la réalisation qui partage le plus d'éléments concrets avec le brief.
   Une seule. Si plusieurs conviennent, prends celle dont le secteur est le plus
   proche. Il y a toujours un choix par défaut : ne renvoie jamais "aucune".
   Vérifie d'abord la congruence de nature avant de comparer les détails : le
   champ "Nature du projet" de chaque fiche (produit avec utilisateurs /
   automatisation interne / reprise technique...) doit être compatible avec ce
   que décrit le brief. Une réalisation d'automatisation interne (pipeline de
   données, script, enrichissement) n'est PAS un exemple pertinent pour un
   brief qui décrit un produit avec utilisateurs (SaaS, application,
   plateforme), et inversement : ne choisis jamais une réalisation dont la
   nature diffère de celle du brief, même si elle a l'air impressionnante ou
   qu'un détail secondaire semble coller.

3. BROUILLON : 8 À 10 RAPPROCHEMENTS CANDIDATS (interne, à ne pas renvoyer)
   Liste-toi mentalement 8 à 10 rapprochements possibles entre le brief et la
   réalisation choisie. Creuse au-delà des évidences de secteur ou de
   catégorie de produit (SaaS, marketplace, multi-rôles, abonnement, fondateur
   non technique, besoin de SEO/responsive...) : ces traits génériques
   décrivent la moitié des projets du portefeuille, ils ne comptent pas comme
   de vrais rapprochements à ce stade, juste comme point de départ.

4. TRI : NE GARDER QUE LES RAPPROCHEMENTS RARES
   Élimine tout rapprochement de l'étape 3 qui décrirait presque aussi bien
   N'IMPORTE QUELLE AUTRE réalisation de la banque, ou qui s'appliquerait à la
   plupart des projets du même secteur. Ne garde que ceux qui tiennent à une
   combinaison précise et peu commune : une fonctionnalité précise dans un
   contexte précis, la même contrainte technique pour la même raison, le même
   choix d'architecture face à la même situation de départ, un même détail
   opérationnel spécifique. Le test : si ce rapprochement collerait presque
   aussi bien à une autre réalisation de la banque, il ne compte pas.
   Retiens EXACTEMENT 3 rapprochements si tu en as 3 qui passent ce test. Si tu
   n'en trouves que 1 ou 2 de vraiment distinctifs, n'en garde que 1 ou 2 :
   mieux vaut peu de liens précis qu'une liste qui pourrait décrire n'importe
   quel projet.

5. RÉDACTION (uniquement les rapprochements retenus à l'étape 4)
   Formulation factuelle, à la deuxième personne quand tu parles du prospect
   ("Comme vous, ...", "Votre besoin de ... rejoint ..."). Chaque puce nomme le
   détail précis côté brief ET le détail précis côté réalisation, pas juste la
   catégorie commune. Une à deux phrases par puce, jamais plus : si le
   rapprochement demande un paragraphe pour être justifié, c'est qu'il n'est
   pas assez direct, retravaille-le ou écarte-le.
   - Uniquement ce qui est étayé par le brief ET par la fiche de la réalisation.
   - Aucune métrique, durée ou résultat qui ne figure pas dans la fiche.
   - Pas de superlatif, pas de promesse. Un rapprochement, pas un argumentaire.

6. CONFIANCE DU CHOIX
   Une fois la réalisation choisie, évalue toi-même la solidité du
   rapprochement avec le brief : "forte" si plusieurs rapprochements précis et
   rares ont passé le tri de l'étape 4, "moyenne" si un seul rapprochement
   vraiment distinctif a passé ce tri, "faible" si aucun rapprochement de
   l'étape 3 n'était assez rare et que tu ne gardes que des points génériques
   de secteur ou de catégorie de produit. Ne gonfle jamais ce niveau pour
   paraître plus convaincant.

Réponds UNIQUEMENT avec un objet JSON, sans texte autour, sans bloc markdown :
{
  "realisation_id": "un des id ci-dessus, exactement",
  "brief_links": ["Premier rapprochement rare.", "Deuxième.", "Troisième si un 3e passe le tri."],
  "confidence": "forte" | "moyenne" | "faible"
}

${COMMON_AI_RULES}`;

export const DEFAULT_HERO_CTA_PROMPT = `Tu es un expert en copywriting B2B et en psychologie de la vente. Tu travailles
pour Léman IT, un studio de développement fullstack solo dirigé par Thibault, qui
accompagne des fondateurs non techniques dans la construction de leurs logiciels
métier sur mesure.

Brief du prospect, déposé sur une plateforme de freelancing :
${BRIEF_PLACEHOLDER}

Tu génères le titre et le sous-titre du hero d'ouverture de la page ; les
badges qui suivent ne sont PAS générés ici (voir note plus bas). C'est la
première chose lue par le prospect, elle doit lui donner l'impression que
cette page a été écrite pour lui, pas qu'un gabarit générique a été rempli
avec son brief.

1. PROFILAGE (analyse interne, à restituer dans "profile")
   Observe comment le prospect écrit :
   - Niveau technique : termes précis (stack, API, webhook...) ou vague et
     fonctionnel ?
   - Signaux d'anxiété : mots qui trahissent une peur, du délai, du contrôle,
     de la qualité, d'une mauvaise expérience passée ("éviter", "garantir",
     "être sûr que"...).
   - Signaux d'aspiration : mots qui expriment une vision, une ambition, un
     désir fort ("créer", "révolutionner", "je veux que", "imaginer"...).
   - Silences révélateurs : qu'est-ce qu'il n'évoque pas alors qu'on s'y
     attendrait (délai, maintenance, utilisateurs) ?
   - Niveau d'urgence : calendrier, deadline, formulations d'impatience ?
   - Ce qu'il valorise : rapidité, contrôle, qualité, autonomie future,
     confiance, impact ?

   Déduis ses 3 à 5 préoccupations dominantes, classées par intensité.

${DISC_STYLE_ANALYSIS}

2. RÉDACTION DU HERO
   RÈGLE ABSOLUE : ce hero vend LE PROJET du prospect (ce que son produit
   devient, ce qu'il permet une fois en ligne), jamais la façon dont Thibault
   travaille ou le chiffre. Le cadrage, le prix fixe, le périmètre V1/V2, les
   jalons : c'est la méthode de collaboration, elle a sa place dans la FAQ et
   plus bas sur la page, jamais dans title/subtitle. Si tu te surprends à
   écrire "cadré", "prix fixe", "périmètre", "V1 réaliste" ou toute variante
   dans le title ou le subtitle, tu es en train de vendre le chiffrage au lieu
   du projet : reviens au produit lui-même.

   Avant d'écrire, identifie UN détail concret et spécifique au PRODUIT décrit
   par CE brief (le nom ou la nature exacte du produit, un mot que le prospect
   a lui-même employé pour UNE fonctionnalité précise, le type d'utilisateur
   final qu'il vise) — jamais un détail sur le déroulé du projet, le
   chiffrage ou la méthode de travail. Ce détail doit apparaître, littéralement
   ou reformulé de façon reconnaissable, dans le title ou le subtitle : c'est ce
   qui empêche le hero de ressembler à un gabarit générique rempli au hasard.

   BROUILLON (interne, à ne pas renvoyer) : formule 3 à 4 titres candidats
   différents, tous ancrés dans le détail concret ci-dessus mais sous des
   angles ET des CONSTRUCTIONS GRAMMATICALES distincts (pas seulement un mot
   différent dans le même moule) : par exemple un qui nomme directement ce qui
   devient acquis ou maîtrisé (affirmation, sans négation), un qui nomme le
   mécanisme concret qui absorbe le risque, un centré sur le type
   d'utilisateur final, un sur la fonctionnalité précise. Si tes 3-4 candidats
   se lisent tous comme "Votre [produit], sans [risque]" avec seulement la fin
   qui change, ce n'est pas un vrai brouillon : recommence avec des
   constructions qui n'ont pas la même syntaxe. Applique à chacun le test de
   l'étape 4 ("vaudrait-il pour n'importe quel autre brief du même secteur ?")
   et ne retiens que le candidat qui le réussit le mieux avant de continuer.
   Même logique de brouillon-puis-tri que pour le choix de la réalisation dans
   le prompt d'étude de cas.

   Exemples pour calibrer le niveau d'exigence, jamais à recopier ni à
   adapter mot pour mot, et surtout pas à traiter comme LE patron à reproduire
   (remarque qu'ils n'utilisent pas la même construction) : pour un brief
   d'agence immobilière qui veut un portail de recherche, "Une plateforme
   immobilière moderne et efficace" est trop générique (vaudrait pour
   n'importe quelle agence). Deux façons différentes de la rendre spécifique
   et rassurante, toutes deux valables : "La recherche de biens filtrée et
   cartographiée, sans relance téléphonique inutile" (par négation de ce qui
   disparaît) ou "Chaque bien reste filtré et localisé, à jour depuis votre
   espace agence" (par affirmation directe de ce qui est acquis, sans
   négation). Choisis la construction selon ce que CE brief précis rend
   vérifiable, jamais par réflexe de répéter la même structure.

   - title : 6 à 10 mots, ancré dans le désir ou la peur centrale du prospect
     ET dans le détail concret identifié ci-dessus, jamais dans une liste de
     fonctionnalités ni dans une formule abstraite qui irait pour n'importe
     quel projet du même secteur. N'ouvre pas systématiquement sur "Votre
     [type de produit]," : ce n'est qu'une des constructions possibles, pas le
     réflexe par défaut. Calibre le ton sur le style DISC détecté :
     - D : direct, annonce le résultat concret dès le titre, sans détour ni
       justification.
     - I : chaleureux, peut nommer l'enthousiasme pour CE projet précisément.
     - S : posé, rassurant, nomme ce qui devient sûr ou accompagné plutôt
       qu'une performance à atteindre.
     - C : factuel, précis, ancré sur un élément vérifiable du brief plutôt
       que sur une promesse.
     Dans tous les cas, la négation ("sans risque", "sans mauvaise surprise")
     n'est qu'UNE option de construction parmi d'autres, jamais le réflexe par
     défaut : nommer ce qui devient acquis, ou décrire le mécanisme concret
     qui répond à l'inquiétude, fonctionnent tout aussi bien.
   - emphasis : la sous-chaîne EXACTE de "title" (recopiée caractère pour
     caractère) qui porte l'idée centrale, à mettre en valeur visuellement. Un
     mot ou un court groupe de mots, jamais toute la phrase.
   - subtitle : une phrase de 20 mots maximum qui reformule la promesse dans le
     langage du prospect, en gardant le détail concret si le title ne l'a pas
     déjà porté, avec le même calibrage par style DISC que le title (D : va au
     fait ; I : ton vivant ; S : rassure sur l'accompagnement ; C : précise un
     mécanisme ou un chiffre vérifiable du brief).

   Sous le titre et le sous-titre, la page affiche 3 informations concrètes
   du projet (prix fixe, délai, date de démarrage) : elles ne
   sont PAS générées ici, elles sont calculées à chaque affichage à partir de
   la grille tarifaire réelle, pour ne jamais raconter un chiffre différent de
   celui du tableau des tarifs plus bas sur la page. N'évoque ni prix, ni
   délai, ni date de démarrage dans le title ou le subtitle : c'est déjà dit
   juste en dessous.

3. ${buildAntiSlopCheck("title et subtitle")}
   Pour tout élément sous 35/50, réécris-le pour qu'il cite ou évoque un
   élément que TOI seul, en lisant CE brief précis, pouvais savoir.

   VÉRIFICATION VEND-LE-PROJET (avant de répondre) : relis title et subtitle.
   S'ils parlent du cadrage, du prix, du périmètre V1/V2 ou de la méthode de
   travail plutôt que du produit et de ce qu'il permet, réécris-les : ce hero a
   vendu le chiffrage au lieu du projet.

Règles :
- Ne génère ni badges, ni bouton, ni texte de CTA : ils sont fixes ou calculés
  hors de ce prompt.
- Ne génère pas d'eyebrow : la ligne au-dessus du titre est fixe elle aussi.
${COMMON_AI_RULES}

Réponds UNIQUEMENT avec un objet JSON, sans texte autour, sans bloc markdown :
{
  "profile": "3 préoccupations dominantes avec leur intensité relative, style DISC dominant et 2-3 indices textuels qui le justifient. 5 lignes maximum, pour validation interne : n'apparaît jamais sur la page publique.",
  "disc_style": "D" | "I" | "S" | "C",
  "disc_secondary": "D" | "I" | "S" | "C" | null,
  "disc_confidence": "faible" | "modere",
  "title": "...",
  "emphasis": "sous-chaîne exacte de title",
  "subtitle": "..."
}`;

export const DEFAULT_FAQ_PROMPT = `Tu es un expert en copywriting B2B et en psychologie de la vente. Tu travailles
pour Léman IT, un studio de développement fullstack solo dirigé par Thibault, qui
accompagne des fondateurs non techniques dans la construction de leurs logiciels
métier sur mesure.

Brief du prospect, déposé sur une plateforme de freelancing :
${BRIEF_PLACEHOLDER}

Tranche de budget qu'il a sélectionnée sur la plateforme (pas forcément écrite
dans le brief) :
${BUDGET_PLACEHOLDER}

Banque de valeur Léman IT (seule source autorisée pour toute fonctionnalité ou
bénéfice cité dans les réponses ; traduis et reformule, ne recopie jamais le
libellé anglais tel quel) :
${MESSAGING_HOUSE_PLACEHOLDER}

Procède en trois étapes.

1. PROFILAGE (analyse interne, à restituer dans "profile")
   Observe comment le prospect écrit :
   - Niveau technique : termes précis (stack, API, webhook...) ou vague et
     fonctionnel ?
   - Signaux d'anxiété : quels mots trahissent une peur (délai, contrôle,
     qualité, mauvaise expérience passée) ?
   - Silences révélateurs : qu'est-ce qu'il n'évoque pas alors qu'on s'y
     attendrait (délai, maintenance, utilisateurs) ?
   - Niveau d'urgence : calendrier, deadline, formulations d'impatience ?
   - Ce qu'il valorise : rapidité, contrôle, qualité, autonomie future,
     confiance ?
   Déduis 3 à 5 préoccupations dominantes, classées de la plus intense à la
   moins intense. N'invente rien qui ne soit pas déductible du texte du brief.

   PRIORITÉ ABSOLUE : si le brief contient des questions explicites posées PAR
   le prospect lui-même ("merci de préciser si...", "recommandez-vous...",
   "merci de justifier...", un arbitrage nommé du type "qu'est-ce qui relève
   d'une V1 réaliste et ce qui doit être prévu en V2"), ce sont les
   préoccupations les plus fiables qui soient, plus fiables que n'importe
   quelle anxiété déduite du ton : AU MOINS UNE de tes 3 à 5 préoccupations
   dominantes doit en reprendre une, même si elle paraît technique plutôt
   qu'émotionnelle. Une question explicite du brief vaut mieux que dix
   signaux d'anxiété interprétés.

${DISC_STYLE_ANALYSIS}

2. SÉLECTION DANS LA BANQUE DE VALEUR
   Pour chaque préoccupation détectée, choisis la ligne de la banque de valeur
   (Problem/Feature/Benefit) la plus pertinente. Ne fabrique pas de
   préoccupation qui ne serait pas détectable dans le brief : la FAQ reflète CE
   prospect, pas un prospect générique.

3. RÉDACTION DE LA FAQ (5 à 7 questions/réponses)
   Ordonne les questions de la préoccupation la plus intense (étape 1) à la
   moins intense : la première question lue doit être celle qui pèse le plus
   dans la décision du prospect, pas une question d'intendance secondaire.
   Avant de finaliser, relis la liste complète : si deux questions
   couvriraient la même objection sous un angle à peine différent, fusionne-les
   ou remplace la moins forte par un angle vraiment distinct.

   VÉRIFICATION SPÉCIFICITÉ (avant de finaliser la liste) : si les
   préoccupations retenues se limitent à cadrage du projet, suivi
   d'avancement, autonomie après le lancement et qualité du code, ce sont les
   4 mêmes qui reviendraient pour n'importe quel brief de développement web :
   tu n'as pas creusé ce qui est propre à CE brief. Relis-le une fois de plus
   à la recherche d'une question explicite qu'il pose, d'un arbitrage de
   périmètre nommé sur une fonctionnalité précise, ou d'un choix technique
   qu'il demande de trancher (voir PRIORITÉ ABSOLUE de l'étape 1) : au moins
   une question de la FAQ doit en découler, même si le vocabulaire reste
   générique (voir règle ci-dessous).
   - Questions à la première personne, dans le registre exact du prospect
     (formel si le brief est formel, direct s'il est direct).
   - Réponses directes, 40 à 60 mots maximum, sans jargon, sans sur-vente, sans
     superlatif creux. Chaque réponse ancre une fonctionnalité de la banque de
     valeur dans un bénéfice concret. Calibre le ton sur le style DISC détecté
     à l'étape 1, dans la limite de 40-60 mots : D va droit au fait, sans
     justification longue ; I garde une pointe de chaleur dans la formulation ;
     S insiste sur l'accompagnement et la disponibilité ; C reste factuel et
     peut citer un mécanisme précis si la banque de valeur le permet.
   - VOCABULAIRE VOLONTAIREMENT GÉNÉRIQUE (règle inverse de celle du hero : le
     hero exige du détail concret propre au brief, la FAQ exige l'inverse,
     parce qu'ici c'est une objection universelle qu'on rassure, pas une
     promesse qu'on rend crédible) : le CHOIX des questions reflète le
     profilage de CE brief, mais leur FORMULATION doit rester générique, comme
     une FAQ standard qui vaudrait pour n'importe quel projet logiciel.
     N'emploie jamais le vocabulaire spécifique du brief (son secteur, son type
     de produit, ses mots à lui : "mes agences", "ma plateforme immobilière",
     "mes patients"...). Dis "mon projet", "mon produit", "mes utilisateurs",
     jamais plus précis. Objectif : qu'on ne puisse pas deviner, à la lecture de
     la FAQ seule, de quel brief elle a été tirée.

   Deux questions systématiques, toujours incluses quelle que soit la
   détection :
   a. Une question sur la façon de commencer sans s'engager à l'aveugle sur
      tout le projet, formulée autour du risque relationnel et de
      l'engagement progressif. Réponse ancrée sur la ligne "a paid 500€,
      two-day scoping step" de la banque de valeur (étape de cadrage payante
      à 500 €, avant tout engagement de développement, entièrement déduite du
      premier jalon si la collaboration continue dans les 30 jours).
      Reprends ces chiffres exactement (500 €, deux jours, 30 jours) : ne les
      remplace jamais par une autre durée ni un autre montant, et ne présente
      jamais cette étape comme gratuite ou comme une "semaine d'essai" — elle
      est payante dès le départ, seulement déduite ensuite.
   b. Une question sur la propriété du code et la sortie du contrat, même si ce
      n'est pas évoqué dans le brief : c'est une objection latente systématique
      à ce niveau de ticket. Réponse ancrée sur la ligne "100% code and
      infrastructure ownership".

   Gestion du budget (selon ${BUDGET_PLACEHOLDER}) :
   - Tranche "+ 10k" ou équivalent : aucune question liée au budget ou au
     paiement.
   - Tranche "500 € à 10k", "< 500 €", ou non précisée : inclus une question
     sur les facilités de paiement, formulée autour de l'engagement progressif
     (jalons, pas de paiement intégral au départ), jamais sur le montant total
     ou le tarif.

Règles :
- N'ancre aucun engagement, fonctionnalité ou bénéfice hors de la banque de
  valeur fournie ci-dessus. Le vocabulaire volontairement générique de l'étape
  3 reste la seule exception à l'interdiction des formules creuses ci-dessous :
  ici la généricité est un choix délibéré, pas un signe de paresse.
${COMMON_AI_RULES}

Réponds UNIQUEMENT avec un objet JSON, sans texte autour, sans bloc markdown :
{
  "profile": "3 préoccupations dominantes avec leur intensité relative, style DISC dominant et 2-3 indices textuels qui le justifient. 5 lignes maximum, pour validation interne : n'apparaît jamais sur la page publique.",
  "disc_style": "D" | "I" | "S" | "C",
  "disc_secondary": "D" | "I" | "S" | "C" | null,
  "disc_confidence": "faible" | "modere",
  "items": [
    { "question": "...", "answer": "..." }
  ]
}`;

// Offre sans phase de cadrage payante (option admin `hideScoping`, petits
// projets) : la ligne "scoping step" de la banque de valeur est retirée et
// cette consigne, ajoutée en fin de prompt, prime sur la question
// systématique de la FAQ qui s'y ancre.
export function stripScopingFromMessagingHouse(messagingHouse: string): string {
  return messagingHouse
    .split("\n")
    .filter((line) => !/scoping step/i.test(line))
    .join("\n");
}

const NO_SCOPING_NOTE = `

CONSIGNE PRIORITAIRE (prime sur toute instruction contraire ci-dessus) : ce
projet n'a PAS de phase de cadrage payante. Ne mentionne jamais d'étape de
cadrage payante, de "500 €" ni de montant déduit du premier jalon. Si une
question porte sur la façon de commencer sans s'engager à l'aveugle, ancre la
réponse sur le prix fixe validé avant le démarrage et le paiement par jalons
validés, jamais sur un cadrage.`;

/**
 * Injecte le brief (remplace {{brief}}, sinon l'ajoute à la fin), l'année en
 * cours (remplace {{year}}), les enjeux stratégiques (remplace {{enjeux}} ;
 * sinon un texte neutre), l'URL de la proposition (remplace
 * {{url_proposition}}), la banque de valeur (remplace {{messaging_house}}), les
 * sections disponibles (remplace {{sections_disponibles}}), la banque de
 * réalisations (remplace {{realisations}}), la tranche de budget du prospect
 * (remplace {{budget}}) et la fourchette prix/délai réelle de la grille
 * tarifaire (remplace {{fourchette_prix}} / {{fourchette_delai}} ; repli
 * explicite "(fourchette non disponible)" si la grille n'existe pas encore,
 * pour que le prompt puisse sauter le paragraphe commercial sans improviser).
 */
/**
 * Consigne de langue placée en FIN de prompt (après le brief, pour garder le
 * préfixe commun du cache Kimi) : les consignes de section restent écrites en
 * français et disent « en français » ; en anglais, celle-ci prime (2026-10-15).
 */
export function languageDirective(language: "fr" | "en" | undefined): string {
  if (language !== "en") return "";
  return `\n\nOUTPUT LANGUAGE: ENGLISH. This overrides every instruction above that asks for French ("en français", French date or number formats, translating into French). Write every text value of the output in natural, professional English, as a native speaker would, in the same voice and register the instructions describe. Keep JSON keys, enum values and identifiers exactly as specified. Dates in English format ("12 March 2026"). Amounts in euros written the English way ("€4,700", "€4,700 – €7,050"). Durations in days ("13 days"). Keep proper names, product names, URLs and figures unchanged. Greetings and sign-offs are translated too: never write "Bonjour" or "Bonsoir", open with "Hello," (or "Hello <first name>,") wherever the instructions say "Bonjour,".`;
}

export function buildPromptWithBrief(
  prompt: string,
  brief: string,
  opts: {
    year?: number;
    enjeux?: string | null;
    urlProposition?: string | null;
    messagingHouse?: string | null;
    sectionsDisponibles?: string | null;
    realisations?: string | null;
    budget?: string | null;
    priceRange?: string | null;
    delayRange?: string | null;
    hideScoping?: boolean;
    language?: "fr" | "en";
  } = {}
): string {
  const { year = new Date().getFullYear(), enjeux, urlProposition } = opts;
  // Les demandes de test envoyées par l'agent Hermès sont préfixées
  // "Test Hermès : " dans `initialRequest` (convention côté Hermès pour les
  // repérer en admin). Retiré ici, au seul point de passage du brief vers
  // tous les prompts, pour que l'IA ne le prenne jamais pour un nom réel
  // (observé : Stitch l'utilisait comme nom d'organisation / texte de logo
  // dans les maquettes des enjeux).
  const trimmed = brief.trim().replace(/^test\s+herm[eè]s\s*:\s*/i, "");
  let out = prompt.split(YEAR_PLACEHOLDER).join(String(year));

  const enjeuxText =
    (typeof enjeux === "string" ? enjeux.trim() : "") || "(pas encore formalisés)";
  out = out.split(ENJEUX_PLACEHOLDER).join(enjeuxText);

  out = out
    .split(URL_PROPOSITION_PLACEHOLDER)
    .join(urlProposition?.trim() || "(lien de la proposition)");

  const messagingHouse = opts.hideScoping
    ? stripScopingFromMessagingHouse(opts.messagingHouse ?? "")
    : opts.messagingHouse;
  out = out
    .split(MESSAGING_HOUSE_PLACEHOLDER)
    .join(
      messagingHouse?.trim() ||
        "(banque de valeur non fournie : n'avance aucun engagement ni garantie chiffrée)"
    );

  out = out
    .split(SECTIONS_DISPONIBLES_PLACEHOLDER)
    .join(
      opts.sectionsDisponibles?.trim() ||
        "(liste non fournie : reste vague sur le contenu de la page)"
    );

  out = out
    .split(REALISATIONS_PLACEHOLDER)
    .join(opts.realisations?.trim() || "(banque de réalisations non fournie)");

  out = out.split(BUDGET_PLACEHOLDER).join(opts.budget?.trim() || "(non précisée)");

  out = out
    .split(PRICE_RANGE_PLACEHOLDER)
    .join(opts.priceRange?.trim() || "(fourchette non disponible)");
  out = out
    .split(DELAY_RANGE_PLACEHOLDER)
    .join(opts.delayRange?.trim() || "(fourchette non disponible)");

  if (out.includes(BRIEF_PLACEHOLDER)) {
    out = out.split(BRIEF_PLACEHOLDER).join(trimmed);
  } else {
    out = `${out}\n\nBrief de la demande :\n${trimmed}`;
  }
  if (opts.hideScoping) out += NO_SCOPING_NOTE;
  out += languageDirective(opts.language);

  return out;
}
